import React, { useState, useEffect, useCallback } from 'react';
import {
  IconMenu2, IconLayoutDashboard, IconMail, IconBell,
  IconCalendarEvent, IconSettings, IconUser, IconDeviceFloppy,
  IconChevronRight, IconShield, IconLock, IconPlus, IconTrash,
} from '@tabler/icons-react';
import api from '../api/api';

/* 아이콘 코드 → 컴포넌트 매핑 */
const ICON_MAP = {
  IconHome: IconLayoutDashboard,
  IconLayoutList: IconLayoutDashboard,
  IconMail: IconMail,
  IconBell: IconBell,
  IconCalendarEvent: IconCalendarEvent,
  IconSettings: IconSettings,
  IconUser: IconUser,
};

export default function FrontMenuPage({ me, showToast }) {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    icon: 'IconHome',
    path: '/',
    sortOrder: 0,
  });

  const isMaster = me?.role === 'MASTER';

  /* ── API 조회: user-web 메뉴 목록 ── */
  const fetchMenus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/menus-front');
      setMenus(res.data.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
      setLoadError(null);
    } catch (err) {
      const msg = `메뉴 조회 실패 (${err.response?.status ?? '네트워크 오류'})`;
      setLoadError(msg);
      showToast(msg, 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  /* ── 신규 메뉴 추가 ── */
  const handleAddMenu = async () => {
    if (!formData.code.trim() || !formData.name.trim() || !formData.path.trim()) {
      showToast('모든 필드를 입력하세요.', 'warning');
      return;
    }

    setSaving(true);
    try {
      await api.post('/menus-front', formData);
      showToast('메뉴가 추가되었습니다.');
      setFormData({ code: '', name: '', icon: 'IconHome', path: '/', sortOrder: 0 });
      await fetchMenus();
    } catch (err) {
      showToast(`추가 실패: ${err.response?.data?.message || '오류 발생'}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* ── 메뉴 수정 ── */
  const handleUpdateMenu = async (id) => {
    setSaving(true);
    try {
      await api.put(`/menus-front/${id}`, formData);
      showToast('메뉴가 수정되었습니다.');
      setEditingId(null);
      setFormData({ code: '', name: '', icon: 'IconHome', path: '/', sortOrder: 0 });
      await fetchMenus();
    } catch (err) {
      showToast(`수정 실패: ${err.response?.data?.message || '오류 발생'}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* ── 메뉴 삭제 ── */
  const handleDeleteMenu = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/menus-front/${id}`);
      showToast('메뉴가 삭제되었습니다.');
      await fetchMenus();
    } catch (err) {
      showToast(`삭제 실패: ${err.response?.data?.message || '오류 발생'}`, 'danger');
    }
  };

  /* ── 메뉴 클릭 시 폼 채우기 ── */
  const handleEditMenu = (menu) => {
    setEditingId(menu.id);
    setFormData({
      code: menu.code,
      name: menu.name,
      icon: menu.icon,
      path: menu.path || '/',
      sortOrder: menu.sortOrder || 0,
    });
  };

  /* ── 폼 초기화 ── */
  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ code: '', name: '', icon: 'IconHome', path: '/', sortOrder: 0 });
  };

  return (
    <div className="page-body">
      <div className="container-xl">

        {/* 상단 헤더 */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="text-muted small">사용자 웹 메뉴 관리</div>
            <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>FRONT 메뉴 설정</h2>
          </div>
        </div>

        {/* 로딩 */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" style={{ width: 40, height: 40 }} />
            <div className="text-muted mt-2">데이터 로딩 중...</div>
          </div>
        )}

        {/* 오류 */}
        {!loading && loadError && (
          <div className="alert alert-danger d-flex align-items-center gap-2">
            <IconMenu2 size={20} />
            <div><strong>조회 실패</strong><br /><small>{loadError}</small></div>
          </div>
        )}

        {/* 본문 */}
        {!loading && !loadError && (
          <div className="row g-3">

            {/* ── 좌측: 메뉴 목록 ── */}
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="card-title mb-0 d-flex align-items-center gap-2">
                    <IconMenu2 size={18} /> 메뉴 목록
                  </h4>
                </div>
                <div className="list-group list-group-flush">
                  {menus.length === 0 && (
                    <div className="list-group-item text-muted text-center py-4">메뉴 없음</div>
                  )}
                  {menus.map(menu => {
                    const isEditing = editingId === menu.id;
                    return (
                      <div
                        key={menu.id}
                        className={`list-group-item ${isEditing ? 'active' : ''}`}
                        style={{ cursor: isMaster ? 'pointer' : 'default' }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="flex-grow-1" onClick={() => isMaster && handleEditMenu(menu)}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="badge bg-azure text-white">
                                {menu.code}
                              </span>
                              {menu.isSystem && (
                                <IconLock size={12} className={isEditing ? 'text-white' : 'text-muted'} />
                              )}
                            </div>
                            <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                              {menu.name}
                            </div>
                            <div className={`small ${isEditing ? 'text-white opacity-75' : 'text-muted'}`}
                              style={{ fontSize: 11 }}>
                              {menu.path}
                            </div>
                          </div>
                          {isMaster && (
                            <button
                              className="btn btn-sm btn-ghost-danger"
                              onClick={() => handleDeleteMenu(menu.id)}
                              title="삭제"
                            >
                              <IconTrash size={14} />
                            </button>
                          )}
                          <IconChevronRight size={14} className={isEditing ? 'text-white' : 'text-muted'} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── 우측: 메뉴 폼 ── */}
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title mb-0">
                    {editingId ? '메뉴 수정' : '새 메뉴 추가'}
                  </h4>
                </div>

                <div className="card-body">
                  {!isMaster && (
                    <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                      <IconLock size={14} className="me-1" />
                      MASTER 계정만 메뉴를 관리할 수 있습니다.
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">메뉴 코드</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="예: user-home"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      disabled={!isMaster}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">메뉴 이름</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="예: 홈"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={!isMaster}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">경로</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="예: /"
                      value={formData.path}
                      onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                      disabled={!isMaster}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">아이콘</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="예: IconHome"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      disabled={!isMaster}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">정렬 순서</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={formData.sortOrder}
                      onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                      disabled={!isMaster}
                    />
                  </div>

                  {isMaster && (
                    <div className="d-flex gap-2">
                      {editingId ? (
                        <>
                          <button
                            className="btn btn-primary btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                            onClick={() => handleUpdateMenu(editingId)}
                            disabled={saving}
                          >
                            {saving ? <span className="spinner-border spinner-border-sm" /> : <IconDeviceFloppy size={16} />}
                            수정
                          </button>
                          <button
                            className="btn btn-secondary btn-sm flex-grow-1"
                            onClick={handleCancelEdit}
                            disabled={saving}
                          >
                            취소
                          </button>
                        </>
                      ) : (
                        <button
                          className="btn btn-success btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                          onClick={handleAddMenu}
                          disabled={saving}
                        >
                          {saving ? <span className="spinner-border spinner-border-sm" /> : <IconPlus size={16} />}
                          추가
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
